import { BaseCondition } from './base-condition.model';
import { CrmEntityAttributeOperators, IAttributeOperator, IBetweenValues } from './crm-entities-attributes-operators';
import { CrmEntity, CrmEntityAttribute, CrmEntityLogicalNameEnum, CrmEntityTypeEnum, IAttributeOption } from '../../crm-entities/crm-entities.model';
import { CrmEntityAttributeTypeEnum } from '../../crm-entities/crm-entities.model';
import { TranslateService } from '@ngx-translate/core';
import { ILookupData } from '../../lookup-dialog-component';

export type ConditionRightHandSide = string | IBetweenValues | ILookupData | IAttributeOption | null;

export class Condition extends BaseCondition {
  isValid = true;
  private _entityName: string = null;
  private _entityType: CrmEntityTypeEnum = null;
  private _attributeLogicalName: string = null; //this is the left-hand side of the condition
  private _attributeDisplayName: string = null;
  private _attributeType: CrmEntityAttributeTypeEnum = null; // this could be used for validations in rare cases where the user changed the type of an attribute
                                                             // (by deleting a custom attribute and adding a new one with the same name and different type)
  private _rightHandSide: ConditionRightHandSide = null;
  private _operatorId: string = null;

  get entityType(): CrmEntityTypeEnum {
    return this._entityType;
  }

  set entityType(value: CrmEntityTypeEnum) {
    this.setProperty('entityType', value);
  }

  get attributeLogicalName(): string {
    return this._attributeLogicalName;
  }

  set attributeLogicalName(value: string) {
    this.setProperty('attributeLogicalName', value);
  }

  get attributeDisplayName() {
    return this._attributeDisplayName;
  }

  set attributeDisplayName(value: string) {
    this.setProperty('attributeDisplayName', value);
  }

  get attributeType(): CrmEntityAttributeTypeEnum {
    return this._attributeType;
  }

  set attributeType(value: CrmEntityAttributeTypeEnum) {
    this._attributeType = value;
  }

  get operatorId(): string {
    return this._operatorId;
  }

  set operatorId(value: string) {
    this.setProperty('operatorId', value);
  }

  get entityName(): string {
    return this._entityName;
  }

  set entityName(value: string) {
    this.setProperty('entityName', value);
  }

  get rightHandSide(): any {
    return this._rightHandSide;
  }

  set rightHandSide(value: any) {
    this.setProperty('rightHandSide', value);
  }

  static fromJSON(json: any | string): Condition {
    if (typeof json === 'string') {
      // if it's a string, parse it first
      return JSON.parse(json, Condition.reviver);
    } else {
      // create an instance of the body class
      let elemStyle = new Condition();
      // copy all the fields from the json object
      let res = Object.assign(elemStyle, json);
      return res;
    }
  }

  static reviver(key: string, value: any): any {
    switch (key) {
      case '':
        return Condition.fromJSON(value);
      default:
        return value;
    }
  }

  constructor() {
    super('condition');
  }

  clone() {
    let clonedCondition = new Condition();
    clonedCondition.entityName = this.entityName;
    clonedCondition.entityType = this.entityType;
    clonedCondition.attributeLogicalName = this.attributeLogicalName;
    clonedCondition.attributeDisplayName = this.attributeDisplayName;
    clonedCondition.attributeType = this.attributeType;
    clonedCondition.rightHandSide = this.rightHandSide;
    clonedCondition.operatorId = this.operatorId;
    return clonedCondition;
  }

  generateExpression(): string {
    let operator: IAttributeOperator = CrmEntityAttributeOperators[this._operatorId];
    if (!this.entityType || !this.attributeLogicalName || !operator ||
      (operator && !operator.doesNotRequireRightHandSide && this.rightHandSide === null)) {
      //just a default. trying to render unfilled condition, this should not have happened
      return 'false';
    }
    let attributeLogicalNameToUse = this.attributeLogicalName;
    // TODO: this code should be added once the DynamicEmail suports adding the XXX_entityid for lookup attributes
    //we will be able to do the comparison with the id and not the name (after all there could be a lot of Avi.Cohen entities)
    // if (this.attributeType === 'Lookup') {
    //   attributeLogicalNameToUse = this.attributeLogicalName + '_eneityid';
    // }
    // TODO: end of additon
    let leftHandSide: any = `Recipient.${CrmEntityLogicalNameEnum[this.entityType]}.${attributeLogicalNameToUse.toLowerCase()}.@value[0]`;
    let expressionToReturn = operator.renderFreeMarker(leftHandSide, this.rightHandSide);
    expressionToReturn = `(${expressionToReturn})`;
    return expressionToReturn;
  }

  generateHumanReadableString(translate: TranslateService) {
    let operator = CrmEntityAttributeOperators[this._operatorId];
    if (!this.entityType || !this.attributeLogicalName || !this._operatorId ||
      (operator && !operator.doesNotRequireRightHandSide && this.rightHandSide === null)) {
      //just a default. trying to render unfilled condition, this should not have happened
      return 'false';
    }
    let translatedEntityName = translate.instant('COMMON.CRM_ENTITY_NAMES.' + this.entityName);
    let leftHandSide: any = `${translatedEntityName}.${this.attributeDisplayName}`;
    return operator.renderHumanReadable(translate, leftHandSide, this.rightHandSide);
  }

  generateHtmlForPreview(entities: CrmEntity[], translate: TranslateService): string {

    let html = ``;
    html += this.isValid === true ? `<div class="single-condition-container">` : `<div class="single-condition-container" style="color: #dd1939;">`;
    html += `${this.generateHumanReadableString(translate)}
                </div>`;
    return html;
  }

  getHeight(): number {
    return 0;
  }


  checkValidity(entities: CrmEntity[]): boolean {
    if (entities) {
      let entity: CrmEntity = entities.find(entity => entity && entity.entityName && entity.entityName.toLowerCase() === this.entityName.toLowerCase());
      if (entity) {
        let currentAttribute: CrmEntityAttribute = entity.attributes.find(attribute => attribute.logicalName === this.attributeLogicalName);
        if (currentAttribute) {
          if (currentAttribute.type === this._attributeType) {
            if (currentAttribute.type === 'List') {
              //check that the options of the boolean and the list did not change and include the selected value
              if (!this.rightHandSide) {
                this.isValid = false;
                return this.isValid;
              }

              let rightHandSideAsOption = this.rightHandSide as IAttributeOption;

              let optionFound = currentAttribute.options.find((option: IAttributeOption) => {
                return (option.value === rightHandSideAsOption.value);
              });
              this.isValid = !!optionFound; //convert to boolean result
              return this.isValid;
            }

            this.isValid = true;
          } else {
            this.isValid = false;
          }
        }
        else {
          this.isValid = false;
        }
      }
    }
    return this.isValid;
  }

  checkListOptionsValidity(entities: CrmEntity[]): boolean {
    if (entities) {
      let entity: CrmEntity = entities.find(entity => entity && entity.entityName && entity.entityName.toLowerCase() === this.entityName.toLowerCase());
      if (entity) {
        let currentAttribute: CrmEntityAttribute = entity.attributes.find(attribute => attribute.logicalName === this.attributeLogicalName);
        if (currentAttribute) {
          if (currentAttribute.type === this._attributeType) {
            if (currentAttribute.type === 'List') {
              if (!this.rightHandSide) {
                return false;
              }
              //check that the options of the boolean and the list did not change and include the selected value
              let rightHandSideAsOption = this.rightHandSide as IAttributeOption;

              let optionFound = currentAttribute.options.find((option: IAttributeOption) => {
                return (option.value === rightHandSideAsOption.value);
              });

              return !!optionFound;
            }
          }
        }
      }
    }
    return true;
  }

}
